import {
  GroupIcon,
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  NavContentBlueprint,
  type NavContentNavItem,
  NavContentNavItems,
} from '@backstage/plugin-app-react';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { accessRoleListPermission } from 'adp-access-react';
import { appTranslationRef } from '../../translation';
import { SidebarLogo } from './SidebarLogo';
import { SidebarDivider } from './SidebarDivider';

const navItemPermissions = {
  'page:access': accessRoleListPermission,
  'page:catalog-import': catalogEntityCreatePermission,
} as const;

type SidebarNavItemProps = {
  item: NavContentNavItem;
  text: string;
};

const NavSidebarItem = ({ item, text }: SidebarNavItemProps) => (
  <SidebarItem icon={() => item.icon} to={item.href} text={text} />
);

export const PermissionAwareSidebarItem = ({
  item,
  text,
}: SidebarNavItemProps) => {
  const id = item.node.spec.id;

  const permission = navItemPermissions[id as keyof typeof navItemPermissions];

  if (permission) {
    return (
      <RequirePermission permission={permission} errorPage={<></>}>
        <NavSidebarItem item={item} text={text} />
      </RequirePermission>
    );
  }

  return <NavSidebarItem item={item} text={text} />;
};

const SidebarComponent = ({ navItems }: { navItems: NavContentNavItems }) => {
  const { t } = useTranslationRef(appTranslationRef);

  const translatedTitles: Record<string, string> = {
    'page:catalog': t('root.catalog'),
    'page:scaffolder': t('root.create'),
    'page:api-docs': t('root.apis'),
    'page:techdocs': t('root.docs'),
    'page:catalog-import': t('root.registerExistingComponent'),
    'page:notifications': t('root.notifications'),
    'page:access': t('root.access'),
    'page:catalog-graph': t('root.catalogGraph'),
    'page:argocd': t('root.argoCd'),
    'page:app-visualizer': t('root.visualizer'),
    'page:user-settings': t('root.settings'),
  };

  const renderNavItems = (items: Array<NavContentNavItem | undefined>) => {
    return items
      .filter((item): item is NavContentNavItem => {
        return Boolean(item);
      })
      .sort((a, b) =>
        (translatedTitles[a.node.spec.id] ?? a.title).localeCompare(
          translatedTitles[b.node.spec.id] ?? b.title,
        ),
      )
      .map(item => (
        <PermissionAwareSidebarItem
          key={item.node.spec.id}
          item={item}
          text={translatedTitles[item.node.spec.id] ?? item.title}
        />
      ));
  };

  // Skipped items
  navItems.take('page:search'); // Using search modal instead
  navItems.take('page:catalog-graph'); // Hide catalog graph from sidebar
  navItems.take('page:rbac'); // RBAC is embedded in the Access section

  const menuItems = [
    navItems.take('page:catalog'),
    navItems.take('page:scaffolder'),
  ];
  const appVisualizerItem = navItems.take('page:app-visualizer');
  const userSettingsItem = navItems.take('page:user-settings');
  const remainingItems = navItems.rest();

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label={t('root.search')} icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label={t('root.menu')} icon={<MenuIcon />}>
        {renderNavItems(menuItems)}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {renderNavItems(remainingItems)}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label={t('root.settings')}
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {renderNavItems([appVisualizerItem])}
        <MyGroupsSidebarItem
          singularTitle={t('root.myGroup')}
          pluralTitle={t('root.myGroups')}
          icon={GroupIcon}
        />
        {renderNavItems([userSettingsItem])}
      </SidebarGroup>
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: SidebarComponent,
  },
});
